/**
 * Admin - Inspector Analytics & Performance
 * Detailed analytics and performance metrics for a specific inspector
 */

import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Grid, Card, CircularProgress, Chip, Avatar, Divider, IconButton } from "@mui/material";
import Icon from "@mui/material/Icon";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";

// API
import { adminAPI } from "utils/api";

function InspectorAnalytics() {
  const { inspectorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inspector, setInspector] = useState(null);
  const [analytics, setAnalytics] = useState({
    totalAssignments: 0,
    activeInspections: 0,
    completedInspections: 0,
    rejectedInspections: 0,
    completionRate: 0,
    averageCompletionTime: 0,
    inspectionsByMonth: [],
    inspectionsByStatus: [],
  });

  useEffect(() => {
    if (inspectorId) {
      fetchInspectorAnalytics();
    }
  }, [inspectorId]);

  const fetchInspectorAnalytics = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getInspectorAnalytics(inspectorId);

      setInspector(response.inspector);
      setAnalytics(response.analytics || analytics);
    } catch (error) {
      console.error("Error fetching inspector analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getStatusColor = (isActive) => {
    return isActive !== false ? "success" : "error";
  };

  const getStatusLabel = (isActive) => {
    return isActive !== false ? "Active" : "Inactive";
  };

  // Prepare chart data
  const inspectionsOverTimeChart = {
    labels: analytics.inspectionsByMonth.map((item) => item.month) || [],
    datasets: [
      {
        label: "Inspections",
        tension: 0.4,
        borderWidth: 0,
        pointRadius: 0,
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        fill: true,
        data: analytics.inspectionsByMonth.map((item) => item.count) || [],
      },
    ],
  };

  const inspectionsByStatusChart = {
    labels: analytics.inspectionsByStatus.map((item) => item.status) || [],
    datasets: {
      label: "Inspections",
      data: analytics.inspectionsByStatus.map((item) => item.count) || [],
      backgroundColors: ["info", "success", "warning", "error"],
    },
  };

  const monthlyPerformanceChart = {
    labels: analytics.inspectionsByMonth.map((item) => item.month) || [],
    datasets: {
      label: "Inspections Completed",
      data: analytics.inspectionsByMonth.map((item) => item.completed) || [],
    },
  };

  // Memoize statistics card props to prevent re-renders on scroll
  const statsCardProps = useMemo(
    () => ({
      totalAssignments: {
        count: analytics.totalAssignments,
        percentage: {
          color: "success",
          amount: `${analytics.completedInspections}`,
          label: "completed",
        },
      },
      activeInspections: {
        count: analytics.activeInspections,
        percentage: {
          color: "info",
          amount: `${
            analytics.totalAssignments > 0
              ? Math.round((analytics.activeInspections / analytics.totalAssignments) * 100)
              : 0
          }%`,
          label: "of total",
        },
      },
      completed: {
        count: analytics.completedInspections,
        percentage: {
          color: "success",
          amount: `${analytics.completionRate}%`,
          label: "completion rate",
        },
      },
      rejected: {
        count: analytics.rejectedInspections,
        percentage: {
          color: "error",
          amount: `${
            analytics.totalAssignments > 0
              ? Math.round((analytics.rejectedInspections / analytics.totalAssignments) * 100)
              : 0
          }%`,
          label: "rejection rate",
        },
      },
    }),
    [analytics]
  );

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!inspector) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDBox display="flex" alignItems="center" mb={3}>
            <IconButton onClick={() => navigate("/admin/operations-team")} sx={{ mr: 2 }}>
              <Icon>arrow_back</Icon>
            </IconButton>
            <MDTypography variant="h4" fontWeight="bold">
              Inspector Not Found
            </MDTypography>
          </MDBox>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <MDBox display="flex" alignItems="center">
            <IconButton onClick={() => navigate("/admin/operations-team")} sx={{ mr: 2 }}>
              <Icon>arrow_back</Icon>
            </IconButton>
            <Avatar
              sx={{
                bgcolor: "info.main",
                width: 64,
                height: 64,
                fontSize: "1.5rem",
                fontWeight: "bold",
                mr: 2,
              }}
            >
              {getInitials(inspector.fullName)}
            </Avatar>
            <MDBox>
              <MDTypography variant="h4" fontWeight="bold">
                {inspector.fullName || "N/A"}
              </MDTypography>
              <MDBox display="flex" alignItems="center" mt={0.5}>
                <Chip
                  label={getStatusLabel(inspector.isActive)}
                  color={getStatusColor(inspector.isActive)}
                  size="small"
                  icon={
                    <Icon sx={{ fontSize: "1rem !important" }}>
                      {inspector.isActive !== false ? "check_circle" : "cancel"}
                    </Icon>
                  }
                  sx={{ mr: 1 }}
                />
                <MDTypography variant="body2" color="text.secondary">
                  Inspector
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
          <MDBox>
            <MDButton
              variant="outlined"
              color="info"
              component="a"
              href={`mailto:${inspector.email || ""}`}
              startIcon={<Icon>email</Icon>}
            >
              Send Email
            </MDButton>
          </MDBox>
        </MDBox>

        {/* Contact Info */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2.5}>
            <MDTypography variant="h6" fontWeight="bold" mb={2}>
              Contact Information
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MDBox display="flex" alignItems="center" mb={1}>
                  <Icon sx={{ fontSize: "1.2rem", color: "text.secondary", mr: 1 }}>email</Icon>
                  <MDTypography
                    variant="body2"
                    component="a"
                    href={`mailto:${inspector.email || ""}`}
                    sx={{
                      color: "primary.main !important",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "primary.dark !important",
                      },
                    }}
                  >
                    {inspector.email || "N/A"}
                  </MDTypography>
                </MDBox>
              </Grid>
              {inspector.phone && (
                <Grid item xs={12} sm={6}>
                  <MDBox display="flex" alignItems="center" mb={1}>
                    <Icon sx={{ fontSize: "1.2rem", color: "text.secondary", mr: 1 }}>phone</Icon>
                    <MDTypography variant="body2" color="text.secondary">
                      {inspector.phone}
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </Card>

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="info"
              icon="assignment"
              title="Total Assignments"
              count={statsCardProps.totalAssignments.count}
              percentage={statsCardProps.totalAssignments.percentage}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="warning"
              icon="schedule"
              title="Active Inspections"
              count={statsCardProps.activeInspections.count}
              percentage={statsCardProps.activeInspections.percentage}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="success"
              icon="check_circle"
              title="Completed"
              count={statsCardProps.completed.count}
              percentage={statsCardProps.completed.percentage}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="error"
              icon="cancel"
              title="Rejected"
              count={statsCardProps.rejected.count}
              percentage={statsCardProps.rejected.percentage}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          {/* Inspections Over Time */}
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={3}>
                  Inspections Over Time
                </MDTypography>
                <DefaultLineChart
                  icon={{ color: "info", component: "trending_up" }}
                  title="Monthly Inspections"
                  description="Number of inspections assigned per month"
                  height="300px"
                  chart={inspectionsOverTimeChart}
                />
              </MDBox>
            </Card>
          </Grid>

          {/* Inspections by Status */}
          <Grid item xs={12} lg={4}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={3}>
                  Inspections by Status
                </MDTypography>
                <DefaultDoughnutChart
                  icon={{ color: "info", component: "pie_chart" }}
                  title="Status Distribution"
                  description="Breakdown of inspection statuses"
                  height="300px"
                  chart={inspectionsByStatusChart}
                />
              </MDBox>
            </Card>
          </Grid>

          {/* Monthly Performance */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={3}>
                  Monthly Performance
                </MDTypography>
                <ReportsBarChart
                  color="info"
                  title="Completed Inspections"
                  description="Number of inspections completed each month"
                  date="Last 6 months"
                  chart={monthlyPerformanceChart}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Performance Metrics */}
        <Card sx={{ mt: 3 }}>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="bold" mb={3}>
              Performance Metrics
            </MDTypography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: "info.lighter",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold" color="info.main">
                    {analytics.completionRate}%
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Completion Rate
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: "success.lighter",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold" color="success.main">
                    {analytics.averageCompletionTime > 0
                      ? `${analytics.averageCompletionTime} days`
                      : "N/A"}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Avg. Completion Time
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: "warning.lighter",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold" color="warning.main">
                    {analytics.activeInspections}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Currently Active
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: "primary.lighter",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold" color="primary.main">
                    {analytics.totalAssignments}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Total Assignments
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default InspectorAnalytics;
